import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import type { ProCustomer, ProEmailCategory } from '@erp/data-access/interfaces';
import { useDataService } from '@/lib/service-context';

/**
 * Interrupteurs des emails de notification.
 *
 * Le lien « se désabonner » du pied des emails écrit dans la même colonne
 * (`pro_customers.email_optout`) : les deux chemins mènent au même état, il
 * n'y a pas de préférence « email » séparée de la préférence « app ».
 */
const CATEGORIES: Array<{
  value: Exclude<ProEmailCategory, 'all'>;
  labelKey: string;
  label: string;
  descKey: string;
  desc: string;
}> = [
  {
    value: 'orders',
    labelKey: 'pro.settings.email_orders',
    label: 'Suivi de commande',
    descKey: 'pro.settings.email_orders_desc',
    desc: 'Confirmée, emballée, expédiée, livrée.',
  },
  {
    value: 'commission',
    labelKey: 'pro.settings.email_commission',
    label: 'Commission et solde',
    descKey: 'pro.settings.email_commission_desc',
    desc: 'À chaque mouvement de votre portefeuille.',
  },
  {
    value: 'recap',
    labelKey: 'pro.settings.email_recap',
    label: 'Bilans périodiques',
    descKey: 'pro.settings.email_recap_desc',
    desc: 'Vos gains chaque mois, vos clients chaque semaine.',
  },
  {
    value: 'catalog',
    labelKey: 'pro.settings.email_catalog',
    label: 'Nouveautés',
    descKey: 'pro.settings.email_catalog_desc',
    desc: 'Nouveaux produits et nouvelles vidéos de formation.',
  },
];

export function EmailPreferences({ proCustomer }: { proCustomer: ProCustomer | null }) {
  const { t } = useTranslation();
  const ds = useDataService();
  const qc = useQueryClient();
  const [saved, setSaved] = useState(false);

  const optout = proCustomer?.emailOptout ?? [];
  const allOff = optout.includes('all');

  const save = useMutation({
    mutationFn: (next: ProEmailCategory[]) => ds.proCustomers.setEmailPrefs({ optout: next }),
    onSuccess: () => {
      setSaved(true);
      qc.invalidateQueries({ queryKey: ['pro-customer'] });
      qc.invalidateQueries({ queryKey: ['pro-auth'] });
    },
  });

  const toggle = (cat: Exclude<ProEmailCategory, 'all'>) => {
    setSaved(false);
    // « all » est un raccourci hérité du lien de désabonnement global : dès
    // qu'on touche un interrupteur, on le convertit en liste explicite pour
    // que les autres catégories restent réglables une par une.
    const base: ProEmailCategory[] = allOff
      ? CATEGORIES.map((c) => c.value)
      : optout.filter((c): c is Exclude<ProEmailCategory, 'all'> => c !== 'all');
    const next = base.includes(cat) ? base.filter((c) => c !== cat) : [...base, cat];
    save.mutate(next);
  };

  return (
    <div>
      <label
        className="text-[14px] font-medium mb-1.5 block"
        style={{ color: 'var(--pro-text-muted)' }}
      >
        {t('pro.settings.email_title', 'Emails de notification')}
      </label>
      <p className="text-[12px] mb-3" style={{ color: 'var(--pro-text-grey)' }}>
        {t(
          'pro.settings.email_desc',
          'Les notifications restent toujours visibles dans la cloche. Ces réglages ne changent que ce qui vous est envoyé par email.',
        )}
      </p>

      <div className="space-y-2">
        {CATEGORIES.map((c) => {
          const on = !allOff && !optout.includes(c.value);
          return (
            <button
              key={c.value}
              type="button"
              role="switch"
              aria-checked={on}
              disabled={save.isPending}
              onClick={() => toggle(c.value)}
              className="w-full flex items-start gap-3 text-left p-3 rounded-xl transition-colors hover:bg-neutral-50 disabled:opacity-50"
              style={{ border: '1px solid var(--pro-border)' }}
            >
              <span
                className="mt-0.5 shrink-0 inline-flex items-center rounded-full p-0.5 transition-colors"
                style={{
                  width: 38,
                  height: 22,
                  background: on ? 'var(--pro-primary)' : 'var(--pro-border)',
                }}
              >
                <span
                  className="block rounded-full bg-white transition-transform"
                  style={{
                    width: 18,
                    height: 18,
                    transform: on ? 'translateX(16px)' : 'translateX(0)',
                  }}
                />
              </span>
              <span className="flex-1 min-w-0">
                <span
                  className="block text-[14px] font-semibold"
                  style={{ color: 'var(--pro-text-dark)' }}
                >
                  {t(c.labelKey, c.label)}
                </span>
                <span
                  className="block text-[12px] mt-0.5"
                  style={{ color: 'var(--pro-text-grey)' }}
                >
                  {t(c.descKey, c.desc)}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      {save.isPending && (
        <p
          className="text-[12px] mt-2 inline-flex items-center gap-1.5"
          style={{ color: 'var(--pro-text-grey)' }}
        >
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          {t('pro.settings.email_saving', 'Enregistrement…')}
        </p>
      )}
      {saved && !save.isPending && (
        <p className="text-[12px] mt-2" style={{ color: 'var(--pro-success)' }}>
          {t('pro.settings.email_saved', 'Préférences enregistrées.')}
        </p>
      )}
      {save.isError && (
        <p className="text-[12px] mt-2" style={{ color: 'var(--pro-danger)' }}>
          {t(
            'pro.settings.email_save_error',
            'Impossible d’enregistrer vos préférences. Réessayez.',
          )}
        </p>
      )}
    </div>
  );
}
